import { Box, Skeleton, Typography } from '@mui/material';
import React, { FC, useState } from 'react';

import useUserChannelSubscriptions from './hooks/useUserChannelSubscriptions';
import UserChannelSubscriptionsFilters from './UserChannelSubscriptionsFilters';
import UserChannelSubscriptionsTable from './UserChannelSubscriptionsTable';
import {
    ALL_SUBSCRIPTION_FILTER_VALUE,
    filterSubscriptions,
    sortSubscriptions,
    toSubscriptionRows,
} from './utils';
import {
    SubscriptionStatusFilter,
    SubscriptionTypeFilter,
    UserChannelSubscriptionsProps,
} from './types';

const EmptySubscriptionsMessage: FC<{ children: React.ReactNode }> = ({
    children,
}) => (
    <Box
        sx={{
            border: 1,
            borderColor: 'divider',
            px: 2,
            py: 3,
        }}
    >
        <Typography color="textSecondary">{children}</Typography>
    </Box>
);

const UserChannelSubscriptions: FC<UserChannelSubscriptionsProps> = ({
    isAdmin,
    userId,
}) => {
    const [subscriptionState, setSubscriptionState] =
        useState<SubscriptionStatusFilter>(ALL_SUBSCRIPTION_FILTER_VALUE);
    const [subscriptionType, setSubscriptionType] =
        useState<SubscriptionTypeFilter>(ALL_SUBSCRIPTION_FILTER_VALUE);
    const { loading, data, error } = useUserChannelSubscriptions({
        isAdmin,
        userId,
    });

    if (!isAdmin || !userId) {
        return null;
    }

    const subscriptions = sortSubscriptions(
        toSubscriptionRows(data?.subscriptions || [])
    );
    const filteredSubscriptions = filterSubscriptions(subscriptions, {
        subscriptionState,
        subscriptionType,
    });

    const hasLoadedWithoutError = !loading && !error;
    const hasSubscriptions = subscriptions.length > 0;

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
                Channel subscriptions
            </Typography>
            <Typography color="textSecondary" sx={{ mb: 2 }}>
                Current and previous channel subscriptions for this user.
            </Typography>
            {loading && <Skeleton variant="rounded" height={96} />}
            {error && (
                <Typography color="error">
                    Unable to load channel subscriptions.
                </Typography>
            )}
            {hasLoadedWithoutError && !hasSubscriptions && (
                <EmptySubscriptionsMessage>
                    No channel subscriptions found.
                </EmptySubscriptionsMessage>
            )}
            {hasLoadedWithoutError && hasSubscriptions && (
                <>
                    <UserChannelSubscriptionsFilters
                        subscriptionState={subscriptionState}
                        subscriptionType={subscriptionType}
                        onSubscriptionStateChange={setSubscriptionState}
                        onSubscriptionTypeChange={setSubscriptionType}
                    />
                    {filteredSubscriptions.length === 0 && (
                        <EmptySubscriptionsMessage>
                            No channel subscriptions match the selected filters.
                        </EmptySubscriptionsMessage>
                    )}
                    {filteredSubscriptions.length > 0 && (
                        <UserChannelSubscriptionsTable
                            subscriptions={filteredSubscriptions}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default UserChannelSubscriptions;
